import { Suspense, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import ErrorBoundary from 'src/components/ErrorBoundary/ErrorBoundary';
import DefaultSource from 'src/components/Sources/Source/DefaultSource';
import SourceCard from 'src/components/Sources/Source/SourceCard';
import { useSources } from 'src/hooks/api';
import { useSidebar } from 'src/hooks/useSidebar';
import { handleAddInternetSources, handleAddSources } from 'src/store/actions/sources';
import type { Source as SourceType } from 'src/types';
import { useAppDispatch } from 'universal-scripts';

import './SourcesMobile.sass';

interface SourcesProps {
	sources: SourceType[];
	internetSources: SourceType[];
	messageId: string;
}

const SourcesMobile = ({ messageId, sources = [], internetSources = [] }: SourcesProps) => {
	const dispatch = useAppDispatch();
	const { handleShowSidebar } = useSidebar();

	const handleShowSources = () => {
		dispatch(handleAddSources(messageId, sources));
		dispatch(handleAddInternetSources(messageId, internetSources));
		handleShowSidebar();
	};

	const usedSources = useMemo(() => sources?.filter((s) => s?.position) || [], [sources]);
	const sourcesUrl = useMemo(() => usedSources?.map((s) => s?.url), [usedSources]);
	const fullSources = useSources(sourcesUrl, `${messageId}-normal-mobile`);

	const usedSourcesInternet = useMemo(() => internetSources?.filter((s) => s?.position) || [], [internetSources]);
	const sourcesUrlInternet = useMemo(() => usedSourcesInternet?.map((s) => s?.url), [usedSourcesInternet]);
	const fullSourcesInternet = useSources(sourcesUrlInternet, `${messageId}-internet-mobile`);

	const totalUsedSourcesCount = usedSources.length + usedSourcesInternet.length;

	const renderedNormalSources =
		fullSources?.map((source, index) => {
			const originalSource = usedSources?.[index];
			return (
				<SourceCard
					isOutdated={originalSource?.outdated}
					isSimple
					isUnused={!originalSource?.position}
					source={source}
					index={originalSource?.position}
					key={`normal-${index}`}
				/>
			);
		}) || [];

	const renderedInternetSources =
		fullSourcesInternet?.map((source, index) => {
			const originalSource = usedSourcesInternet?.[index];
			return (
				<SourceCard
					isOutdated={originalSource?.outdated}
					isSimple
					isUnused={!originalSource?.position}
					source={source}
					index={originalSource?.position}
					key={`internet-${index}`}
				/>
			);
		}) || [];

	const separator = renderedInternetSources.length ? (
		<>
			<hr /> <FormattedMessage id="internet-sources" />
		</>
	) : (
		[]
	);

	const allRenderedSources = [...renderedNormalSources, separator, ...renderedInternetSources];
	return (
		<section id="chat-sources-mobile">
			<header>
				<strong className="sources-amount">
					<FormattedMessage id="chat-sources.amount" values={{ n: totalUsedSourcesCount }} />
				</strong>
				<button className="source-button" onClick={handleShowSources}>
					<span>
						<FormattedMessage id="view-all" />
					</span>
				</button>
			</header>
			<nav className="sources-list done">{allRenderedSources.slice(0, renderedInternetSources.length ? 4 : 3)}</nav>
		</section>
	);
};
const SourcesMobileWrapper = ({ sources, internetSources, messageId }: SourcesProps) => (
	<ErrorBoundary fallback={<div />}>
		<Suspense
			fallback={
				<section id="chat-sources-mobile">
					<header>
						<strong className="sources-amount">
							<FormattedMessage id="chat-sources.amount" values={{ n: '?' }} />
						</strong>
					</header>
					<div className="sources-list">
						<DefaultSource isSimple loading />
					</div>
				</section>
			}
		>
			<SourcesMobile messageId={messageId} sources={sources} internetSources={internetSources} />
		</Suspense>
	</ErrorBoundary>
);

export default SourcesMobileWrapper;
